import { components } from "@/__openapi__/backend/schema";
import type { AssertTrue, ValidateEnumIsSubtype } from "@/domains/typing/types";

/**
 * Session Types
 */

export type SmartViewWithSmartCards = components["schemas"]["SmartViewResponseSchema"];
export type SmartView = Omit<SmartViewWithSmartCards, "smart_cards">;

export type GenericSmartCard = SmartViewWithSmartCards["smart_cards"][number];

export enum SmartCardKind {
  NoteList = "NOTE_LIST",
  MentionedByNotesList = "MENTIONED_BY_NOTES_LIST",
  CollectionList = "COLLECTION_LIST",
  EventTimeline = "EVENT_TIMELINE",
  Live = "LIVE",
  Markdown = "MARKDOWN",
  MeetingBriefing = "MEETING_BRIEFING",
  Unknown = "UNKNOWN",
}

export type SmartCardKindValues = (typeof SmartCardKind)[keyof typeof SmartCardKind];

type _ValidateSmartCardKindValues = AssertTrue<
  ValidateEnumIsSubtype<SmartCardKind, SmartCardKindValues>
>;

export type NoteListSmartCardValue = components["schemas"]["NoteListValueSchema"];
export type MentionedByNotesListSmartCardValue = components["schemas"]["MentionedByValueSchema"];
export type CollectionListSmartCardValue = components["schemas"]["CollectionListValueSchema"];
export type EventTimelineSmartCardValue = components["schemas"]["TimelineValueSchema"];
export type LiveNoteItem = components["schemas"]["LiveNoteSmartCardItem"] & {
  recording_offset_seconds?: number | null;
};
export type LiveSmartCardValue = Omit<components["schemas"]["LiveValueSchema"], "items"> & {
  items: LiveNoteItem[];
};
export type MarkdownSmartCardValue = components["schemas"]["MarkdownValueSchema"];
export type MeetingBriefingSmartCardValue = {
  title?: string | null;
  markdown?: string | null;
  note_items: NoteSmartCardItem[];
  reasoning?: string | null;
  tool_calls: Record<string, unknown>[];
  timing?: Record<string, unknown> | null;
  connected_calendar_event_id?: string | null;
};
export type UnknownSmartCardValue = {
  [key: string]: unknown;
};

type BaseSmartCardFields = Omit<GenericSmartCard, "kind" | "value"> & {
  sort_key: string | null;
};

export type NoteListSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.NoteList;
  value: NoteListSmartCardValue;
};

export type MentionedByNotesListSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.MentionedByNotesList;
  value: MentionedByNotesListSmartCardValue;
};

export type CollectionListSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.CollectionList;
  value: CollectionListSmartCardValue;
};

export type EventTimelineSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.EventTimeline;
  value: EventTimelineSmartCardValue;
};

export type LiveSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.Live;
  value: LiveSmartCardValue;
};

export type MarkdownSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.Markdown;
  value: MarkdownSmartCardValue;
};

export type MeetingBriefingSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.MeetingBriefing;
  value: MeetingBriefingSmartCardValue;
};

export type UnknownSmartCard = BaseSmartCardFields & {
  kind: SmartCardKind.Unknown;
  value: UnknownSmartCardValue;
};

export type SmartCard =
  | NoteListSmartCard
  | MentionedByNotesListSmartCard
  | CollectionListSmartCard
  | EventTimelineSmartCard
  | LiveSmartCard
  | MarkdownSmartCard
  | MeetingBriefingSmartCard
  | UnknownSmartCard;

export type NoteSmartCardItem = components["schemas"]["NoteSmartCardItem"];
export type CollectionSmartCardItem = components["schemas"]["CollectionSmartCardItem"];
export type EventSmartEntitySmartCardItem = components["schemas"]["EventSmartEntitySmartCardItem"];

export type SmartCardItem =
  | NoteSmartCardItem
  | CollectionSmartCardItem
  | EventSmartEntitySmartCardItem
  | LiveNoteItem;
